import { useQuery } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

export function useAccountProfileQuery(params: { impersonationContext?: Api.IImpersonationContext }) {
	const userSession = useUserSession();
	return useQuery<Api.AccountProfile | null>({
		queryFn: async () => {
			try {
				return await userSession.webServiceHelper.callAsync<Api.AccountProfile>(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `accountProfile`,
						impersonationContext: params.impersonationContext,
					}),
					'GET'
				);
			} catch (error) {
				// AccountProfile is optional - a 404 means the account hasn't created a profile yet
				// Treat this as a valid state by returning null instead of throwing
				if (typeof error === 'object' && error != null && 'systemCode' in error && error.systemCode === 404) {
					return null;
				}
				throw error;
			}
		},
		queryKey: [`accountProfile`, params.impersonationContext],
		// Fix for #17636: Disable retries for 404 responses (which are handled gracefully by returning null)
		// but keep retry protection for real errors like network failures and 500s
		retry: (failureCount, error) => {
			// Don't retry 404s - account profile doesn't exist yet
			if (typeof error === 'object' && error != null && 'systemCode' in error && error.systemCode === 404) {
				return false;
			}
			// Allow retries for other errors (network failures, 500s, etc.)
			return failureCount < 3;
		},
	});
}

export function invalidateAccountProfileQueries(params: { impersonationContext?: Api.IImpersonationContext }) {
	return reactQueryClient.invalidateQueries({
		queryKey: [`accountProfile`, params.impersonationContext],
	});
}
